import React from 'react'
import get from 'lodash/get'

import { PriceDisplay } from '@fielded/shared-ui'
import { MEMBERSHIPS } from '@fielded/fs-api/lib/location/tools'
import { getThresholds, getThresholdRelativeToMOS } from '@fielded/fs-api/lib/forecast/tools/read/psm-thresholds'

import { formatDate, isShelflifeResellerApp } from '../../../van-shared/utils/utils'
import { CUTOFF_COPY } from '../components/constants'
import { isDateRelevant } from '../../retailer/shipments/helpers'
import { ORDER_DISPLAY_STATUS, SHIPMENT_STATUS_TO_ORDER_DISPLAY_STATUS } from './constants'

export const computeProductMoq = ({ soh, consumed, periodMonths, packsToOrder, programId, budgetAmount, reorderPoint }) => {
  if (soh === 0 && consumed === 0) return 0
  // Budget path may allocate healthy-range products (above reOrder) — trust the allocation.
  if (budgetAmount && packsToOrder > 0) return packsToOrder
  // Per-product reorder point from forecast takes precedence over program thresholds.
  if (reorderPoint != null) {
    return soh < reorderPoint ? packsToOrder : 0
  }
  const thresholds = getThresholds(programId)
  const amc = consumed / (periodMonths || 1)
  const mos = amc === 0 ? Infinity : soh / amc
  const thresholdKey = getThresholdRelativeToMOS(thresholds, mos)
  const needsReorder = thresholdKey === 'critical' || thresholdKey === 'ok'
  return needsReorder ? packsToOrder : 0
}

export const CASHBACK_MULTIPLIER = 2
const CASHBACK_MULTIPLIER_LAST_DAY = '2025-01-31'
const CASHBACK_INELIGIBLE_STATES = ['katsina']

export const diffLists = (a, b) => {
  const setA = a.reduce((set, x) => set.add(x), new Set())
  const setB = b.reduce((set, x) => set.add(x), new Set())

  const removed = a.filter(x => !setB.has(x))
  const added = b.filter(x => !setA.has(x))

  return [added, removed]
}

export const processDeliveryOptions = ({
  isFastDelivery,
  country,
  createdAt,
  deliveryDate,
  customDeliveryDateOption,
  appliedDeliveryFee,
  countryId
}) => {
  const currentDay = new Date()
  const currentHour = currentDay.getHours()
  const cutoffTimes = {
    ng: 12,
    ke: 20
  }

  const getHour = (timestamp) => {
    const inputDate = new Date(timestamp)
    return inputDate.getHours()
  }

  const orderHour = getHour(createdAt)

  let deliveryLabel
  let deliveryDateOptions = []

  if (isFastDelivery) {
    const today = {
      value: formatDate(new Date(), 'snapshotId'),
      label: cutoffTimes[countryId] > currentHour ? CUTOFF_COPY.oneDay : CUTOFF_COPY.twoDay,
      subLabel: <PriceDisplay value={appliedDeliveryFee} country={countryId} currencySymbol />
    }
    deliveryDateOptions.unshift(today)

    deliveryLabel = (country === 'ng' && orderHour < 12) || (country === 'ke' && orderHour < 20) ? CUTOFF_COPY.oneDay : CUTOFF_COPY.twoDay
  } else {
    deliveryDateOptions.push(customDeliveryDateOption)
    deliveryLabel = formatDate(deliveryDate, 'long')
  }
  return { deliveryLabel, deliveryDateOptions }
}

export const shouldSeeCashBack = (location, isPaused) => {
  const state = get(location, 'location.state')
  const membershipType = get(location, 'membership')

  const isExcludedState = CASHBACK_INELIGIBLE_STATES.includes(state)
  const isBasicUser = membershipType === MEMBERSHIPS.BASIC
  const isClassicPAYGOnlyUserPaused = membershipType === MEMBERSHIPS.CLASSIC_PAYG_ONLY && isPaused

  return !isExcludedState && (isBasicUser || isClassicPAYGOnlyUserPaused)
}

export const shouldSeeDoubleCashBack = () => {
  return isDateRelevant(new Date(CASHBACK_MULTIPLIER_LAST_DAY))
}

export const getProcessedCashBack = (cashback) => {
  const withDoubleCashback = shouldSeeDoubleCashBack()
  if (isNaN(cashback) || !withDoubleCashback) {
    return cashback
  }
  return (cashback * CASHBACK_MULTIPLIER)
}

export const getOrderDisplayStatus = (order) => {
  const { shipmentStatus, closedStatus } = order
  const fallbackStatus = ORDER_DISPLAY_STATUS.IN_REVIEW

  if (closedStatus === ORDER_DISPLAY_STATUS.CANCELLED) {
    return closedStatus
  }

  if (!shipmentStatus) {
    return fallbackStatus
  }

  return SHIPMENT_STATUS_TO_ORDER_DISPLAY_STATUS[shipmentStatus] || fallbackStatus
}

export {
  isShelflifeResellerApp
}
